// Rows as the Supabase tables return them (snake_case), shared by the real and demo backends.
import type { SelectedMuscle } from '../lib/selection.ts';

export type Avatar = 'male' | 'female';
export type ConsentKind = 'terms' | 'health_data' | 'trainer_sharing';
/** Bump when the privacy policy or consent text changes (same value as the iOS app). */
export const CONSENT_VERSION = '2026-10-17';

export interface Profile {
  id: string;
  full_name: string;
  avatar: Avatar;
  timezone: string;
  onboarded_at: string | null;
}

export interface Session {
  id: string;
  kind: 'trainer' | 'solo';
  activity_type: string;
  source: 'healthkit' | 'calendar' | 'manual';
  status: 'pending' | 'logged' | 'dismissed';
  started_at: string;
  ended_at: string | null;
  intensity: number | null;
  recovery: number | null;
  note: string | null;
  muscles: SelectedMuscle[];
}

export interface Checkin { day: string; rating: number }
export interface Upcoming { id: string; calendar_event_id: string; title: string; starts_at: string }

export interface TrainerNote {
  id: string;
  body: string;
  muscle_ids: string[];
  raise_next_session: boolean | null;
  raised_at: string | null;
  created_at: string;
  trainer_name: string;
}

export interface TrainerLink {
  id: string;
  trainer_name: string;
  share_muscles: boolean;
  share_ratings: boolean;
  share_solo: boolean;
  share_health: boolean;
}

export type Sharing = Pick<TrainerLink, 'share_muscles' | 'share_ratings' | 'share_solo' | 'share_health'>;

/** Everything the app reads or writes. SupabaseBackend talks to the project; DemoBackend stays in the browser. */
export interface Backend {
  readonly isDemo: boolean;
  /** Current user id, or null when signed out. */
  userId(): Promise<string | null>;
  /** Sign-in email of the current user. */
  email(): Promise<string | null>;
  onAuthChange(cb: () => void): () => void;
  sendCode(email: string): Promise<void>;
  verifyCode(email: string, code: string): Promise<void>;
  signOut(): Promise<void>;

  profile(): Promise<Profile | null>;
  updateProfile(p: Partial<Omit<Profile, 'id'>>): Promise<void>;
  consents(): Promise<Set<ConsentKind>>;
  grantConsent(kind: ConsentKind): Promise<void>;
  revokeConsent(kind: ConsentKind): Promise<void>;

  sessions(since: Date): Promise<Session[]>;
  logSession(s: Session): Promise<void>;
  checkins(sinceDay: string): Promise<Checkin[]>;
  saveCheckin(c: Checkin): Promise<void>;
  upcoming(): Promise<Upcoming[]>;
  /** Replaces the future sessions the digest is timed from (sync_upcoming_sessions). */
  syncUpcoming(events: { calendar_event_id: string; title: string; starts_at: string }[]): Promise<void>;

  notes(): Promise<TrainerNote[]>;
  links(): Promise<TrainerLink[]>;
  updateSharing(linkId: string, sharing: Sharing): Promise<void>;
  revokeLink(linkId: string): Promise<void>;
  createInvite(): Promise<string>;
  deleteAccount(): Promise<void>;
}
